import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  TextField,
  Typography,
  Box,
  Alert,
  Pagination,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
  InputAdornment,
  IconButton,
  Tooltip,
  Skeleton,
  Chip,
  keyframes,
  Zoom,
  Fade,
  Grow
} from '@mui/material';
import { Article } from '../types';
import { Link as RouterLink } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';

const API_URL = 'https://guide.zerdegroup.kz/api';

// Анимация пульсации для поискового поля
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 ${alpha('#2196f3', 0.4)};
  }
  70% {
    box-shadow: 0 0 0 10px ${alpha('#2196f3', 0)};
  }
  100% {
    box-shadow: 0 0 0 0 ${alpha('#2196f3', 0)};
  }
`;

// Анимация появления результатов
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Хук для значения с задержкой
function useDebounceValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Очищаем предыдущий таймер при новом вводе
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    
    // Устанавливаем новый таймер
    timerRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay) as unknown as NodeJS.Timeout;
    
    // Очищаем таймер при размонтировании
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [value, delay]);
  
  return debouncedValue;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(date);
};

const Search: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  
  // Состояния
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Рефы для контроля запросов и эффектов
  const abortControllerRef = useRef<AbortController | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialRenderRef = useRef(true);
  const isClearing = useRef(false);

  // Функция для проверки валидности запроса
  const isValidSearch = (query: string): boolean => {
    return query.trim().length >= 2;
  };

  // Функция выполнения поиска
  const executeSearch = useCallback(async (query: string, page: number = 1) => {
    // Проверка валидности запроса
    if (!isValidSearch(query)) return;
    
    // Отмена предыдущего запроса
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Создаем новый контроллер
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;
    
    // Всегда устанавливаем loading в true перед выполнением запроса
    setLoading(true);
    setHasSearched(false);
    setError(null);
    
    try {
      // Для отладки
      console.log(`Поиск: "${query}", страница: ${page}`);
      
      const response = await fetch(
        `${API_URL}/articles/search?q=${encodeURIComponent(query)}&page=${page}&limit=10`,
        { signal }
      );
      
      if (!response.ok) {
        throw new Error('Ошибка при поиске статей');
      }
      
      const data = await response.json();
      
      if (!signal.aborted) {
        // Устанавливаем результаты
        setArticles(data.articles);
        setTotalPages(data.totalPages);
        setCurrentPage(data.page);
        
        // Небольшая задержка перед обновлением статуса поиска
        // Это гарантирует, что React успеет отрисовать новые данные
        // перед тем, как будет принято решение о отображении "Ничего не найдено"
        setTimeout(() => {
          if (!signal.aborted) {
            setHasSearched(true);
            setLoading(false);
          }
        }, 50);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('Запрос был отменен');
        return;
      }
      
      // Устанавливаем ошибку
      setError(err instanceof Error ? err.message : 'Произошла ошибка при поиске');
      setArticles([]);
      
      // Та же задержка для ошибок
      setTimeout(() => {
        if (abortControllerRef.current?.signal.aborted === false) {
          setHasSearched(true);
          setLoading(false);
        }
      }, 50);
    }
  }, []);

  // Обработчик изменения поля ввода
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Очищаем предыдущий таймер
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    
    // Если запрос пустой, очищаем результаты
    if (value.trim().length === 0) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      isClearing.current = true;
      setSearchParams({});
      setArticles([]);
      setError(null);
      setTotalPages(1);
      setLoading(false);
      setHasSearched(false);
      return;
    }
    
    // Если запрос слишком короткий, не выполняем поиск
    if (value.trim().length < 2) return;
    
    // Показываем загрузку только после задержки, когда уверены, что запрос отправится
    const loadingTimeout = setTimeout(() => {
      if (searchTimeoutRef.current) { // Если таймер поиска всё ещё активен
        setLoading(true);
      }
    }, 300);
    
    // Устанавливаем новый таймер для debounce
    searchTimeoutRef.current = setTimeout(() => {
      // Обновляем URL
      setSearchParams({ q: value });
      // Выполняем поиск
      executeSearch(value, 1);
      
      // Отменяем таймер загрузки, если он ещё не сработал
      clearTimeout(loadingTimeout);
    }, 700) as unknown as NodeJS.Timeout;
  }, [executeSearch, setSearchParams]);

  // Обработчик очистки поля ввода
  const handleClearSearch = useCallback(() => {
    // Отменяем текущий запрос
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Очищаем таймер
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    
    // Помечаем, что происходит очистка
    isClearing.current = true;
    
    // Очищаем все состояния
    setSearchQuery('');
    setSearchParams({});
    setArticles([]);
    setError(null);
    setTotalPages(1);
    setCurrentPage(1);
    setLoading(false);
    setHasSearched(false);
  }, [setSearchParams]);

  // Обработчик изменения страницы
  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    
    if (isValidSearch(searchQuery)) {
      executeSearch(searchQuery, page);
    }
  }, [executeSearch, searchQuery]);

  // Эффект для инициализации поиска из URL
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      
      const queryParam = searchParams.get('q');
      if (queryParam && isValidSearch(queryParam)) {
        executeSearch(queryParam, 1);
      }
    }
  }, [executeSearch, searchParams]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Функция подсветки текста в результатах
  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === query.toLowerCase() ? 
        <mark key={i} style={{ 
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
          padding: '0 2px',
          borderRadius: '2px'
        }}>{part}</mark> : 
        part
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
      <Box 
        sx={{ 
          position: 'relative', 
          minHeight: 300,
          paddingTop: articles.length > 0 ? '20px' : '90px',
          transition: 'padding-top 0.3s ease',
          willChange: 'padding-top'
        }}
      >
        {/* Заголовок в абсолютной позиции */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            opacity: articles.length === 0 && !loading && !error ? 1 : 0,
            transform: articles.length === 0 && !loading && !error ? 'translateY(0)' : 'translateY(-10px)',
            pointerEvents: articles.length === 0 && !loading && !error ? 'auto' : 'none',
            transition: 'opacity 0.25s ease, transform 0.25s ease',
            mb: 4,
            textAlign: 'center',
            willChange: 'transform, opacity'
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              color: 'text.primary',
              letterSpacing: '-0.5px',
              fontSize: { xs: '1.75rem', md: '2.125rem' }
            }}
          >
            Поиск
          </Typography>
        </Box>

        {/* Поле поиска */}
        <Box 
          sx={{ 
            position: 'relative',
            mb: 4,
            zIndex: 2,
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden'
          }}
        >
          <TextField
            fullWidth
            placeholder="Введите поисковый запрос..."
            value={searchQuery}
            onChange={handleSearchChange}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ 
                    color: theme.palette.primary.main,
                    fontSize: 24
                  }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    edge="end"
                    sx={{ 
                      color: 'text.secondary',
                      '&:hover': {
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.08)
                      }
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'background.paper',
                height: { xs: 48, md: 56 },
                fontSize: { xs: '1rem', md: '1.1rem' },
                '& fieldset': {
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                  transition: 'border-color 0.2s ease'
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2
                }
              }
            }}
          />
          {searchQuery.length > 0 && searchQuery.length < 2 && (
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                mt: 1,
                ml: 2,
                color: 'text.secondary',
                opacity: 1,
                transform: 'translateZ(0)'
              }}
            >
              Введите минимум 2 символа для поиска
            </Typography>
          )}
        </Box>

        {/* Контейнер результатов */}
        <Box sx={{ position: 'relative' }}>
          {loading ? (
            <Stack spacing={2}>
              {[1, 2, 3].map((i) => (
                <Skeleton 
                  key={i}
                  variant="rounded" 
                  height={140}
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    animation: 'none'
                  }} 
                />
              ))}
            </Stack>
          ) : error ? (
            <Alert 
              severity="error" 
              sx={{ 
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  fontSize: '1.5rem'
                }
              }}
            >
              {error}
            </Alert>
          ) : articles.length > 0 ? (
            <Box 
              sx={{ 
                opacity: 1,
                visibility: 'visible',
                transition: 'opacity 0.25s ease',
                transform: 'translateZ(0)'
              }}
            >
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mb: 3,
                  color: 'text.secondary',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <SearchIcon sx={{ fontSize: 20 }} />
                Найдено результатов: {articles.length}
              </Typography>
              <Stack spacing={2}>
                {articles.map((article, index) => (
                  <Card 
                    key={article.id}
                    component={RouterLink}
                    to={`/article/${article.category.id}/${article.subcategory.id}/${article.id}`}
                    sx={{
                      textDecoration: 'none',
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.04)}`,
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                      transformStyle: 'preserve-3d',
                      backfaceVisibility: 'hidden',
                      transform: 'translateZ(0)',
                      '&:hover': {
                        transform: 'translateY(-2px) translateZ(0)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
                        borderColor: theme.palette.primary.main,
                        '& .MuiTypography-h6': {
                          color: theme.palette.primary.main
                        }
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography 
                        variant="h6" 
                        component="h2"
                        sx={{ 
                          mb: 1.5,
                          fontWeight: 600,
                          color: 'text.primary',
                          transition: 'color 0.2s ease',
                          lineHeight: 1.4,
                          fontSize: { xs: '1.1rem', md: '1.25rem' }
                        }}
                      >
                        {highlightText(article.title, searchQuery)}
                      </Typography>

                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.6
                        }}
                      >
                        {highlightText(article.content, searchQuery)}
                      </Typography>

                      <Box sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap'
                      }}>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            size="small"
                            icon={<CategoryIcon sx={{ fontSize: 16 }} />}
                            label={article.category.title}
                            sx={{ 
                              height: 24,
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              color: 'primary.main',
                              fontWeight: 500,
                              '& .MuiChip-icon': {
                                color: 'primary.main',
                                fontSize: 16
                              }
                            }}
                          />
                          <Chip
                            size="small"
                            icon={<SubdirectoryArrowRightIcon sx={{ fontSize: 16 }} />}
                            label={article.subcategory.title}
                            sx={{ 
                              height: 24,
                              backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                              color: 'secondary.main',
                              fontWeight: 500,
                              '& .MuiChip-icon': {
                                color: 'secondary.main',
                                fontSize: 16
                              }
                            }}
                          />
                        </Stack>
                        <Box sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          ml: 'auto',
                          color: 'text.secondary'
                        }}>
                          <Box sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}>
                            <PersonIcon sx={{ fontSize: 16 }} />
                            <Typography variant="caption">
                              {article.author.login}
                            </Typography>
                          </Box>
                          <Box sx={{ mx: 1, color: 'divider' }}>•</Box>
                          <Box sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}>
                            <AccessTimeIcon sx={{ fontSize: 16 }} />
                            <Typography variant="caption">
                              {formatDate(article.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>

              {totalPages > 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 4
                }}>
                  <Pagination 
                    count={totalPages} 
                    page={currentPage} 
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: 1.5,
                        '&.Mui-selected': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.15)
                          }
                        },
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          ) : hasSearched && searchQuery.length >= 2 ? (
            <Box sx={{ 
              textAlign: 'center',
              py: 8
            }}>
              <SearchIcon sx={{ 
                fontSize: 48, 
                color: 'text.secondary',
                opacity: 0.5,
                mb: 2 
              }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Ничего не найдено
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Попробуйте изменить поисковый запрос
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Container>
  );
};

export default Search; 